<!--
	MIRE-only map overlay: range sliders per curve field plus a "Symbolize" field picker, pushed onto
	the Curves layer as a filter and `line-color` expression (mirrored into `mireExplorerStore` for the legend).
-->
<script lang="ts">
	import { mireRamp, DEFAULT_FEATURE_COLOR } from '#lib/vizPalette.js';
	import type { ExpressionSpecification, FilterSpecification } from 'maplibre-gl';
	import { formatBandNumber } from '#lib/components/map/legend/symbology.js';
	import { geodataStore } from '#lib/stores/geodataStore.svelte.js';
	import { mapDataFilterStore } from '#lib/stores/mapDataFilterStore.svelte.js';
	import { mapInstanceStore } from '#lib/stores/mapInstanceStore.svelte.js';
	import { mireExplorerStore } from '#lib/stores/mireExplorerStore.svelte.js';
	import {
		ECurves,
		EMireModule,
		type IMireSummaryData,
		type IMireSystemLayers
	} from '../interfaces';
	import MultiRangeSlider, {
		type MultiRangeSliderChangeResult
	} from '#lib/components/MultiRangeSlider.svelte';
	import Select from '#lib/components/Select.svelte';
	import ModuleButton from '#lib/components/ModuleButton.svelte';
	import ModuleButtonGroup from '#lib/components/ModuleButtonGroup.svelte';
	import exploreIcon from '#lib/assets/modules/explorer.svg?raw';

	const fieldLabels: Record<string, string> = {
		mire_195_curve_radius_avg: '195: Average curve radius (ft.)',
		mire_195_curve_radius_min: '195: Minimum curve radius (ft.)',
		mire_196_curve_length: '196: Curve length (ft.)',
		mire_199_curve_angle: '199: Curve angle (degrees)'
	};

	const colorTheme = mireRamp;
	const defaultColor = DEFAULT_FEATURE_COLOR;

	type Field = { id: string; label: string; min: number; max: number };

	const layerId = $derived(
		(geodataStore.systemData as IMireSystemLayers | undefined)?.[EMireModule.Curves]?.[
			ECurves.Curves
		]?.layer?.id
	);

	// Per-field min/max from the MIRE summary table.
	const fields = $derived.by<Field[]>(() => {
		const summary = geodataStore.summaryData as IMireSummaryData | undefined;
		const row = summary?.[EMireModule.Curves]?.[0];
		if (!row) return [];
		return Object.keys(fieldLabels).map((id) => ({
			id,
			label: fieldLabels[id],
			min: row[`${id}_min`] as number,
			max: row[`${id}_max`] as number
		}));
	});

	let showPanel = $state(false);
	let selectedIndex = $state<number | null>(null);
	const panelId = $props.id();
	// Sparse per-field overrides; missing entries fall back to the summary min/max.
	let lowerOverrides = $state<Record<number, number>>({});
	let upperOverrides = $state<Record<number, number>>({});

	const rangesLower = $derived(fields.map((f, i) => lowerOverrides[i] ?? f.min));
	const rangesUpper = $derived(fields.map((f, i) => upperOverrides[i] ?? f.max));

	function calculateRangeBreaks(lower: number, upper: number) {
		const band = (upper - lower) / 4;
		return [lower + band, lower + 2 * band, lower + 3 * band];
	}

	// Expression syntax must match `mapDataFilterStore.filter`, or MapLibre's legacy filter parser rejects the combined `['all', …]`.
	const filterExpression = $derived.by<ExpressionSpecification>(() => {
		const expr: ExpressionSpecification = ['all'];
		for (let i = 0; i < fields.length; i++) {
			const field = fields[i];
			const lower = rangesLower[i];
			const upper = rangesUpper[i];
			if (lower !== field.min) expr.push(['>=', ['get', field.id], lower]);
			if (upper !== field.max) expr.push(['<=', ['get', field.id], upper]);
		}
		return expr;
	});

	// Null values keep the default colour; out-of-range values are hidden by `filterExpression`.
	const lineColorExpression = $derived.by<ExpressionSpecification | string>(() => {
		const field = selectedIndex === null ? undefined : fields[selectedIndex];
		if (!field) return defaultColor;
		const [b1, b2, b3] = calculateRangeBreaks(field.min, field.max);
		const value: ExpressionSpecification = ['get', field.id];
		return [
			'case',
			['==', value, null],
			defaultColor,
			['step', value, colorTheme[0], b1, colorTheme[1], b2, colorTheme[2], b3, colorTheme[3]]
		];
	});

	const legendLabels = $derived.by(() => {
		if (selectedIndex === null || !fields[selectedIndex]) return [];
		const field = fields[selectedIndex];
		const edges = [
			rangesLower[selectedIndex],
			...calculateRangeBreaks(field.min, field.max),
			rangesUpper[selectedIndex]
		];
		return edges.slice(1).map((hi, i) => `${formatBandNumber(edges[i])} – ${formatBandNumber(hi)}`);
	});

	// Sole `setFilter` writer for the Curves layer (the legend excludes it), so the global data filter is combined here.
	$effect(() => {
		if (fields.length === 0) return;
		const map = mapInstanceStore.map;
		if (!map || !layerId || !map.getLayer(layerId)) return;
		const explorerEmpty = filterExpression.length === 1 && filterExpression[0] === 'all';
		const combinedFilter: FilterSpecification = explorerEmpty
			? mapDataFilterStore.filter
			: (['all', filterExpression, mapDataFilterStore.filter] as ExpressionSpecification);
		map.setFilter(layerId, combinedFilter);
	});

	$effect(() => {
		if (fields.length === 0) return;
		const map = mapInstanceStore.map;
		if (!map || !layerId || !map.getLayer(layerId)) return;
		mireExplorerStore.lineColorExpression = lineColorExpression;
		mireExplorerStore.stepLabels = legendLabels;
		map.setPaintProperty(layerId, 'line-color', lineColorExpression);
	});

	function onChangeRange({ lower, upper }: MultiRangeSliderChangeResult, index: number) {
		if (lower !== rangesLower[index]) {
			lowerOverrides = { ...lowerOverrides, [index]: lower };
		}
		if (upper !== rangesUpper[index]) {
			upperOverrides = { ...upperOverrides, [index]: upper };
		}
	}

	const symbolizeOptions = $derived(fields.map((f, i) => ({ value: String(i), label: f.label })));

	const numberFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 });
	// Band edges of the symbolised field, so the ramp isn't read by colour alone.
	const rampLabels = $derived.by(() => {
		const field = selectedIndex === null ? undefined : fields[selectedIndex];
		if (!field) return [];
		return [field.min, ...calculateRangeBreaks(field.min, field.max), field.max].map((n) =>
			numberFormat.format(n)
		);
	});
</script>

{#if geodataStore.systemDataReady && fields.length > 0}
	<section class="explorerControl" aria-label="MIRE data explorer">
		<div class="buttonRow">
			<ModuleButtonGroup>
				<ModuleButton
					title="Explore data"
					aria-expanded={showPanel}
					aria-controls={panelId}
					popovertarget={panelId}
					anchorName="--mireExplorer">
					{@html exploreIcon} Explore
				</ModuleButton>
			</ModuleButtonGroup>
		</div>
		<div
			class="explorerPanel"
			id={panelId}
			popover="auto"
			ontoggle={(e) => (showPanel = (e as ToggleEvent).newState === 'open')}>
			<div class="fields">
				{#each fields as field, index (field.id)}
					{@const [code, name] = field.label.split(': ')}
					<span class="code">{code}</span>
					<span>{name}</span>
					<MultiRangeSlider
						id={`range-${index}`}
						lower={rangesLower[index]}
						upper={rangesUpper[index]}
						min={field.min}
						max={field.max}
						step={0.1}
						label={field.label}
						onvalueinput={(result) => onChangeRange(result, index)} />
				{/each}
			</div>
			<Select
				options={symbolizeOptions}
				value={selectedIndex === null ? '' : String(selectedIndex)}
				label="Symbolize"
				placeholder="None"
				inline
				onchange={(value) => (selectedIndex = value === '' ? null : Number(value))} />
			{#if rampLabels.length}
				<div class="ramp">
					<div class="swatches" aria-hidden="true">
						{#each colorTheme as color (color)}
							<div style:background-color={color}></div>
						{/each}
					</div>
					<div class="rampLabels">
						{#each rampLabels as label, i (i)}
							<span>{label}</span>
						{/each}
					</div>
				</div>
			{/if}
		</div>
	</section>
{/if}

<style>
	.explorerControl {
		position: absolute;
		top: var(--ctrl-spacing-top);
		left: var(--ctrl-spacing-sides);
		z-index: var(--z-map-control);
		pointer-events: none;
	}

	.buttonRow {
		pointer-events: all;
	}

	[popover].explorerPanel {
		position: fixed;
		position-anchor: --mireExplorer;
		position-area: block-end span-inline-end;
		position-try-fallbacks: flip-inline;
		inset: auto;
		margin: var(--space-1) 0 0;
		gap: var(--space-4);
		box-shadow: var(--shadow-md);
		border: 1px solid var(--border-color);
		border-radius: var(--radius);
		background: var(--surface-raised);
		padding: var(--space-5) var(--space-6) var(--space-6);
		width: 40rem;
		max-width: calc(100vw - 2 * var(--ctrl-spacing-sides));
		font-size: var(--font-size-sm);

		&:popover-open {
			display: flex;
			flex-direction: column;
		}

		.fields {
			display: grid;
			grid-template-columns: auto 8rem 1fr;
			align-items: start;
			gap: var(--space-5) var(--space-3);
		}

		.code {
			font-weight: var(--weight-bold);
		}
	}

	.ramp {
		.swatches {
			display: flex;
			height: var(--space-4);

			> div {
				flex: 1;
			}
		}

		.rampLabels {
			display: flex;
			justify-content: space-between;
			margin-top: var(--space-1);
			color: var(--text-muted);
			font-size: var(--font-size-xs);
		}
	}
</style>
